import { Alert, Button, Checkbox, Flex, Input, Modal, notification, Divider, Typography } from "antd";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  createPayments,
  createSystemProductOrder,
  getCompany,
  prepareSmsMessage,
  sendSmsMessage,
} from "../../../service";
import { P } from "../../molecules/service-details-modal/ServiceDetailsModal";
import { axiosInstance, checkOnlineOrderingStatus, PATH } from "../../../service/client";
import { RegulaminContent, PolitykaPrywatnosciContent } from "../legal-documents/LegalDocuments";

const { TextArea } = Input;
const { Text } = Typography;


export const MarketingSms: React.FC = () => {
  const { t } = useTranslation();
  const query = useQueryClient();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [quantity, setQuantity] = useState(1000);
  const [textAreaValue, setTextAreaValue] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [docModal, setDocModal] = useState<{ open: boolean; type: "regulamin" | "polityka" | null }>({
    open: false, type: null,
  });

  const showModal = () => {
    setTermsAccepted(false);
    setIsModalOpen(true);
  };

  const handleOk = () => {
    if (!termsAccepted) {
      notification.error({
        message: t("marketingSms.acceptTermsAndPrivacy"),
        placement: "bottomRight",
      });
      return;
    }
    if (quantity >= 1) {
      mutateAsync({
        quantity: quantity,
        type: "SMS",
      });
    } else {
      notification.error({
        message: t("marketingSms.minSmsQuantityOne"),
        placement: "bottomRight",
      });
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setTermsAccepted(false);
  };

  const openDocModal = (type: "regulamin" | "polityka") => {
    setDocModal({ open: true, type });
  };

  const downloadAgreementPdf = async () => {
    try {
      const response = await axiosInstance.get(
        `${PATH}/v1/common_api/admin/online-ordering/agreement/pdf`
      );
      if (response.data?.url) {
        window.open(response.data.url, "_blank");
      }
    } catch {
      notification.error({
        message: t("marketingSms.agreementDownloadFailed"),
        placement: "bottomRight",
      });
    }
  };

  const { data: companyData, isLoading } = useQuery<any, Error>(
    ["/company"],
    () => getCompany()
  );

  const { data: agreementStatus } = useQuery(
    ["/online-ordering/status"],
    () => checkOnlineOrderingStatus(),
    { retry: false }
  );

  const hasSignedAgreement = agreementStatus?.status === "signed";

  const {
    mutateAsync: mutateAsynccreatePayments,
    isLoading: createPaymentsLoading,
  } = useMutation(createPayments, {
    onSuccess: (response) => {
      if (response.status === 201) {
        setIsModalOpen(false);
        if (
          !!response.data.id &&
          (response.data.status === "CONFIRMED" ||
            response.data.status === "PENDING")
        ) {
          if (response.data.content?.transactionData?.link) {
            const targetUrl = response.data.content.transactionData?.link;
            window.location.href = targetUrl;

            setTimeout(() => {
              if (window.location.href !== targetUrl) {
                Modal.info({
                  title: t("marketingSms.goToPayment"),
                  content: (
                    <div>
                      <a href={targetUrl}>Przelewy 24</a>
                    </div>
                  ),
                  footer: <></>,
                });
              }
            }, 1000);
          } else {
            notification.error({
              message: t("marketingSms.somethingWentWrong"),
              placement: "bottomRight",
            });
          }
        }
      }
    },
    onError: () => {
      notification.error({
        message: t("marketingSms.somethingWentWrong"),
        placement: "bottomRight",
      });
    },
  });

  const {
    mutateAsync: sendSmsMessageMutateAsync,
    isLoading: sendSmsMessageMutateIsLoading,
  } = useMutation(sendSmsMessage, {
    onSuccess: () => {
      query.invalidateQueries([`/company`]);
      setTextAreaValue("");
      notification.success({
        message: t("marketingSms.smsSent"),
        placement: "bottomRight",
      });
    },
    onError: () => {
      notification.error({
        message: t("marketingSms.error"),
        placement: "bottomRight",
      });
    },
  });

  const {
    mutateAsync: prepareSmsMessageMutateAsync,
    isLoading: prepareSmsMessageMutateIsLoading,
  } = useMutation(prepareSmsMessage, {
    onSuccess: (data) => {
      if (data?.gsm7Text?.trim()?.length === 0) {
        notification.error({
          message: t("marketingSms.messageContentValidationError"),
          placement: "bottomRight",
        });
        return;
      }
      if (
        companyData.smsLeft <
        Math.ceil(data?.gsm7Text?.length / 150) * data?.validPhones?.length
      ) {
        notification.error({
          message: t("marketingSms.insufficientSms"),
          placement: "bottomRight",
        });
        return;
      }
      Modal.info(
        {
          closable: true,
          title: t("marketingSms.sendInfoTitle"),
          onOk: () => {
            return new Promise<void>((resolve, reject) => {
              try {
                sendSmsMessageMutateAsync({
                  message: data?.gsm7Text,
                  recievers: data?.validPhones,
                }).finally(() => {
                  resolve();
                });
              } catch (error) {
                reject();
              }
            })
          },
          okText: t("marketingSms.confirmSend"),
          content: (
            <Flex
              vertical
              gap={12}
              style={{
                paddingTop: "10px",
              }}
            >
              <P $height="14" $size="16" $weight="600">
                {t("marketingSms.messageText")}
              </P>
              <P
                $height="14"
                $size="16"
                $weight="300"
                style={{
                  marginBottom: "15px",
                }}
              >
                {data?.gsm7Text}
              </P>
              <P $height="14" $size="16" $weight="400">
                {t("marketingSms.recipientsCount", { count: data?.validPhones?.length })}
              </P>
              <P $height="14" $size="16" $weight="400">
                {t("marketingSms.smsUsedCount", {
                  count:
                    Math.ceil(data?.gsm7Text?.length / 150) *
                    data?.validPhones?.length,
                })}
              </P>
            </Flex>
          ),
        }
      );
    },
    onError: () => {
      notification.error({
        message: t("marketingSms.error"),
        placement: "bottomRight",
      });
    },
  });

  const { mutateAsync, isLoading: createIsLoading } = useMutation(
    createSystemProductOrder,
    {
      onSuccess: (response) => {
        if (response.status === 201) {
          mutateAsynccreatePayments({
            amount: parseInt(
              (quantity * companyData.smsPrice * 1.23).toFixed(0)
            ),
            content: {},
            paymentMethodId: parseInt(
              process.env.REACT_APP_SYSTEM_PAYMENT_METHOD_ID || "70"
            ),
            urlReturn: "https://admin.proste.menu/marketing",
            systemProductOrderId: response.data.id,
            email: companyData.email,
          });
        }
      },
      onError: () => {
        notification.error({
          message: t("marketingSms.error"),
          placement: "bottomRight",
        });
      },
    }
  );

  const priceNetto = companyData ? (quantity * companyData.smsPrice / 100) : 0;
  const priceBrutto = companyData ? (quantity * companyData.smsPrice * 1.23 / 100) : 0;
  const pricePerSms = companyData ? (companyData.smsPrice / 100) : 0;

  if (!isLoading)
    return (
      <>
        <div>
          <Flex justify="start" align="center" gap={5}>
            <h4>{t("marketingSms.smsQuantityLabel")}</h4>
            <p>{companyData.smsLeft}</p>
          </Flex>

          {!hasSignedAgreement && (
            <Alert
              message={t("marketingSms.noSignedAgreementTitle")}
              description={t("marketingSms.noSignedAgreementDescription")}
              type="warning"
              showIcon
              style={{ marginBottom: 12, maxWidth: 500 }}
            />
          )}

          <Button type="primary" onClick={showModal} disabled={!hasSignedAgreement}>
            {t("marketingSms.buySms")}
          </Button>
          <Flex justify="start" align="start" vertical>
            <h4
              style={{
                marginBottom: "0px",
              }}
            >
              {t("marketingSms.createCampaignTitle")}
            </h4>
            <p style={{ fontSize: 14, color: "var(--color-text-secondary)", marginBottom: 15 }}>
              {t("marketingSms.campaignDescription")}
            </p>
            <TextArea
              style={{
                width: "400px",
              }}
              value={textAreaValue}
              onChange={(e) => setTextAreaValue(e.target.value)}
            />
            <Button
              type="primary"
              onClick={() => {
                if (textAreaValue)
                  prepareSmsMessageMutateAsync({
                    message: textAreaValue,
                  });
              }}
              loading={
                prepareSmsMessageMutateIsLoading ||
                sendSmsMessageMutateIsLoading
              }
              disabled={
                prepareSmsMessageMutateIsLoading ||
                sendSmsMessageMutateIsLoading ||
                textAreaValue.length === 0
              }
            >
              {t("marketingSms.sendSmsToClients")}
            </Button>
          </Flex>
        </div>

        {/* ── Purchase SMS Modal ── */}
        <Modal
          title={t("marketingSms.buySmsPackageTitle")}
          open={isModalOpen}
          onOk={handleOk}
          onCancel={handleCancel}
          okText={t("marketingSms.goToPaymentButton")}
          okButtonProps={{
            loading: createIsLoading || createPaymentsLoading,
            disabled: createIsLoading || createPaymentsLoading || !termsAccepted || quantity < 1000,
          }}
          cancelButtonProps={{
            disabled: createIsLoading || createPaymentsLoading,
          }}
          width={480}
        >
          <Flex vertical gap={16}>
            {/* Quantity input */}
            <div>
              <Text strong style={{ display: "block", marginBottom: 4 }}>{t("marketingSms.smsAmountLabel")}</Text>
              <Input
                type="number"
                value={quantity}
                min={1000}
                step={100}
                status={quantity < 1000 ? "error" : undefined}
                onChange={(e: any) => {
                  if (Number.isInteger(parseInt(e.target.value)))
                    setQuantity(parseInt(e.target.value));
                }}
              />
              {quantity < 1000 && (
                <Text type="danger" style={{ fontSize: 12, marginTop: 4, display: "block" }}>
                  {t("marketingSms.minPurchaseAmount")}
                </Text>
              )}
            </div>

            {/* Price breakdown */}
            <div style={{
              background: "var(--color-background-secondary, #fafafa)",
              border: "1px solid var(--color-border-light, #e9ecef)",
              borderRadius: 8,
              padding: "12px 16px",
            }}>
              <Flex justify="space-between" style={{ marginBottom: 4 }}>
                <Text type="secondary">{t("marketingSms.pricePerSmsLabel")}</Text>
                <Text>{t("marketingSms.amountNetWithSuffix", { amount: pricePerSms.toFixed(2) })}</Text>
              </Flex>
              <Flex justify="space-between" style={{ marginBottom: 4 }}>
                <Text type="secondary">{t("marketingSms.netAmountLabel")}</Text>
                <Text>{t("marketingSms.amountZl", { amount: priceNetto.toFixed(2) })}</Text>
              </Flex>
              <Flex justify="space-between" style={{ marginBottom: 4 }}>
                <Text type="secondary">{t("marketingSms.vatLabel")}</Text>
                <Text>{t("marketingSms.amountZl", { amount: (priceBrutto - priceNetto).toFixed(2) })}</Text>
              </Flex>
              <Divider style={{ margin: "8px 0" }} />
              <Flex justify="space-between">
                <Text strong>{t("marketingSms.toPayLabel")}</Text>
                <Text strong style={{ fontSize: 16 }}>{t("marketingSms.amountGrossWithSuffix", { amount: priceBrutto.toFixed(2) })}</Text>
              </Flex>
            </div>

            {/* Payment method info */}
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 12px",
              background: "var(--color-info-bg, rgba(24, 144, 255, 0.15))",
              borderRadius: 6,
              border: "1px solid var(--color-border-light, #e9ecef)",
            }}>
              <span style={{ fontSize: 18 }}>💳</span>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t("marketingSms.paymentVia")} <strong>Przelewy24</strong>
              </Text>
            </div>

            <Divider style={{ margin: "4px 0" }} />

            {/* Terms checkbox - required by P24 */}
            <Checkbox
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
            >
              <span style={{ fontSize: 12, lineHeight: 1.5 }}>
                {t("marketingSms.termsAccept")}{" "}
                <a
                  href="#regulamin"
                  onClick={(e) => {
                    e.preventDefault();
                    openDocModal("regulamin");
                  }}
                >
                  {t("marketingSms.termsOfServiceLink")}
                </a>{" "}
                {t("marketingSms.and")}{" "}
                <a
                  href="#polityka"
                  onClick={(e) => {
                    e.preventDefault();
                    openDocModal("polityka");
                  }}
                >
                  {t("marketingSms.privacyPolicyLink")}
                </a>
                . {t("marketingSms.termsRead")}{" "}
                <a
                  href="https://www.przelewy24.pl/regulamin"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("marketingSms.p24TermsLink")}
                </a>{" "}
                {t("marketingSms.and")}{" "}
                <a
                  href="https://www.przelewy24.pl/obowiazek-informacyjny-rodo-platnicy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("marketingSms.p24InfoObligationLink")}
                </a>
                .
              </span>
            </Checkbox>

            {/* Link to signed agreement — fetched via axios with auth */}
            <div style={{
              padding: "8px 12px",
              background: "var(--color-success-bg, rgba(82, 196, 26, 0.15))",
              borderRadius: 6,
              border: "1px solid var(--color-border-light, #e9ecef)",
            }}>
              <Text style={{ fontSize: 12 }}>
                📄{" "}
                <a
                  href="#umowa"
                  onClick={(e) => {
                    e.preventDefault();
                    downloadAgreementPdf();
                  }}
                >
                  {t("marketingSms.downloadSignedAgreement")}
                </a>
              </Text>
            </div>
          </Flex>
        </Modal>

        {/* ── Document viewer modal (regulamin / polityka) ── */}
        <Modal
          title={docModal.type === "regulamin" ? t("marketingSms.termsOfServiceLink") : t("marketingSms.privacyPolicyTitle")}
          open={docModal.open}
          onCancel={() => setDocModal({ open: false, type: null })}
          footer={null}
          width={800}
          styles={{ body: { maxHeight: "70vh", overflowY: "auto", padding: "16px 24px" } }}
        >
          {docModal.type === "regulamin" && <RegulaminContent />}
          {docModal.type === "polityka" && <PolitykaPrywatnosciContent />}
        </Modal>
      </>
    );
  return null;
};
